import React, { Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import FormatResourceStatusLabel from "go-app/components/FormatResourceStatus/FormatResourceStatus";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { FormatDate, convertUTCDateToLocalDate } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { PdfOrientationType } from "go-core/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { OrganizationClientGrantApi, OrganizationClientGrantsProps } from "../../../../services/types";
import { ClientGrantStatus } from "../../../ClientDetails/services/types";
import ClientGrantsCreateOrUpdateModal from "../../components/ClientGrantsCreateModal";

interface ListState extends OrganizationClientGrantsProps {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
	addOrUpdateClientGrantsModal: boolean;
	setAddOrUpdateClientGrantsModal: React.Dispatch<React.SetStateAction<boolean>>;
	organizationName?: string;
	taxIdNo?: string;
	company?: string;
	pdfOrientation?: PdfOrientationType;
	pdfFontSize?: string;
}

const listName = "CLIENT_GRANT";
const resourceType = "CLIENT_GRANT";

const List = ({
	resource,
	mobileActions,
	getClientGrants,
	createClientGrant,
	updateClientGrant,
	addOrUpdateClientGrantsModal,
	setAddOrUpdateClientGrantsModal,
	revokeClientGrant,
	organizationName,
	taxIdNo,
	company,
	pdfOrientation,
	pdfFontSize,
	organizationTimezone,
}: ListState) => {
	const [clientGrantToUpdate, setClientGrantToUpdate] = useState<OrganizationClientGrantApi | undefined>();
	const [clientGrants, setClientGrants] = useState<OrganizationClientGrantApi[]>([]);
	const { t } = useTranslation("lib");
	const segmentContext = useContext(SegmentContext);
	const listParams = useRef<Record<string, any>>({});
	const { addSuccessFlash } = useFlash();

	const onModalHide = () => {
		setAddOrUpdateClientGrantsModal(false);
		setClientGrantToUpdate(undefined);
	};

	if (!resource) return null;
	const data = resource.read();

	let config = {
		fields: [
			{
				id: "client_id",
				name: t("go_component.client_grants.field.client_id.title"),
			},
			{
				id: "granted_scopes",
				name: t("go_component.client_grants.field.scopes.title"),
				disableSorting: true,
			},
			{
				id: "label",
				name: t("go_component.client_grants.field.label.title"),
			},
			{
				id: "status",
				name: t("common.word.status"),
				render: (item: OrganizationClientGrantApi) => (
					<FormatResourceStatusLabel
						status={item.status}
						className={item.status === "REVOKED" ? "danger" : "success"}
						translation={t(`enums.client_grants.status.${item.status}`)}
					/>
				),
				renderExport: (item: OrganizationClientGrantApi) => t(`enums.client_grants.status.${item.status}`),
			},
			{
				id: "created_at",
				name: t("common.word.created_at"),
				render: (item: OrganizationClientGrantApi) =>
					FormatDate(convertUTCDateToLocalDate(item.created_at, organizationTimezone)),
				disableSorting: true,
			},
		],
		actions: [
			{
				name: t("common.action.edit"),
				click: (item: OrganizationClientGrantApi) => {
					setAddOrUpdateClientGrantsModal(true);
					setClientGrantToUpdate(item);
				},
			},
			{
				name: t("common.action.remove"),
				click: async (item: OrganizationClientGrantApi) => {
					if (!item.id) return;

					await revokeClientGrant(item.id);
					addSuccessFlash(t("common.flash.removed"));
				},
				refreshListAfter: true,
				confirmation: {
					use: true,
					title: t("go_component.client_grants.action.key_remove.title"),
					message: t("go_component.client_grants.action.key_remove.help_text"),
				},
				visible: (item: OrganizationClientGrantApi) => item.status !== ClientGrantStatus.REVOKED,
			},
		],
		selectedColumns: ["client_id", "granted_scopes", "label", "status", "created_at"],
		segments: [{ id: "all", name: t("common.word.all"), slug: "all" }],
		exportConfig: {
			title: t("go_component.client_grants.field.export_config.title"),
			filename: t("go_component.client_grants.header.title"),
			organization: organizationName,
			taxIdNo,
			company,
			pdfOrientation,
			pdfFontSize,
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			listParams.current = params;
			return getClientGrants(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	const onRefresh = async () => {
		if (config.fetch) {
			const fetchedClientGrants = await config.fetch(listParams.current);
			setClientGrants(fetchedClientGrants);
		}
	};

	return (
		<>
			<ListData
				config={config}
				emptyList={{ addAction: () => setAddOrUpdateClientGrantsModal(true) }}
				mobileActions={mobileActions}
				data={clientGrants}
				onFetch={(items: OrganizationClientGrantApi[]) => setClientGrants(items)}
			/>
			{addOrUpdateClientGrantsModal && (
				<ClientGrantsCreateOrUpdateModal
					onHide={onModalHide}
					createClientGrant={createClientGrant}
					updateClientGrant={updateClientGrant}
					defaultValues={clientGrantToUpdate}
					onRefresh={onRefresh}
				/>
			)}
		</>
	);
};

const SettingsClientGrantsIndexPage = (props: RouteComponentProps & OrganizationClientGrantsProps) => {
	const [addOrUpdateClientGrantsModal, setAddOrUpdateClientGrantsModal] = useState<boolean>(false);
	const [docsPath, setDocsPath] = useState<string | undefined>();
	const { t } = useTranslation("lib");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const { isMobile } = useWindowSize();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { addFlash } = useFlash();

	useEffect(() => {
		handleChangeTabTitle(t("go_component.client_grants.header.title"));
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
		getDocsPath();
	}, []);

	const getDocsPath = async () => {
		try {
			const newDocsPath = await props.getDocsPath();
			setDocsPath(newDocsPath.api_documentation_url);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [
		{
			title: t("go_component.client_grants.field.docs.title"),
			action: () => window.open(docsPath, "_blank"),
			variant: "light",
		},
		{
			title: t("common.action.add"),
			action: () => setAddOrUpdateClientGrantsModal(true),
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("go_component.client_grants.field.docs.title"),
			action: () => window.open(docsPath, "_blank"),
		},
		{
			title: t("common.action.add"),
			action: () => setAddOrUpdateClientGrantsModal(true),
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("go_component.client_grants.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List
					resource={resource}
					mobileActions={mobileActions}
					addOrUpdateClientGrantsModal={addOrUpdateClientGrantsModal}
					setAddOrUpdateClientGrantsModal={setAddOrUpdateClientGrantsModal}
					{...props}
				/>
			</Suspense>
		</>
	);
};

export default SettingsClientGrantsIndexPage;

// t("enums.client_grants.status.ACTIVE");
// t("enums.client_grants.status.REVOKED");
